import { WarningAltFilled } from '@carbon/icons-react';
import { Banner } from 'component/banner';
import { HorizBox } from 'component/basics';
import {
    colorSurfaceBannerInfo,
    colorSurfaceSecondary,
    colorTextHint,
    colorUtilityKilauea1,
    colorUtilityMum,
    colorUtilitySaguaro2,
} from 'component/color';
import { fromNow } from 'component/date';
import { Heading, UtilityText } from 'component/text';
import { SuspensionStatusType, useMySuspensions } from 'open-modules/zdf/component/suspension/suspension';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useDatastore } from 'util/datastore';
import type { Feature, TLabel } from 'util/stdtypes';
import { t } from 'util/stdtypes';
export const WarnBlockOverviewFeature: Feature = {
    name: 'Warn & Block Overview',
    key: 'warnblockoverviewfeature',
    providesConfig: {
        accountWidgets: [WarnBlockDisplay],
    },
};

const WarnAndBlockStyle = StyleSheet.create({
    outer: {
        marginTop: 40,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        justifyContent: 'flex-start',
    },
    scroll: {
        display: 'flex',
        gap: 16,
        flexDirection: 'column',
        marginTop: 16,
        maxHeight: 400,
    },
    outerCard: {
        display: 'flex',
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        backgroundColor: colorSurfaceSecondary,
        borderRadius: 8,
        margin: 6,
    },
    innerCard: {
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        margin: 12,
        width: 48,
        height: 48,
        borderRadius: 4,
    },
    innerCardColumn: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
});

export function WarnBlockDisplay() {
    const s = WarnAndBlockStyle;
    const datastore = useDatastore();
    const mySuspensions = useMySuspensions();

    const renderedSuspensions = mySuspensions.all.filter((s) => s.suspensionType !== SuspensionStatusType.UNWARNED);

    return (
        <View style={s.outer}>
            <Heading label={t('Warnings and blocks')} type='small' />
            <HorizBox center gap={8}>
                <UtilityText color={colorTextHint} label={t('🔒 Only visible to you')} />
            </HorizBox>
            <ScrollView style={s.scroll}>
                {renderedSuspensions.length <= 0 && (
                    <Banner color={colorSurfaceBannerInfo}>
                        <UtilityText text='No suspensions yet' />
                    </Banner>
                )}
                {renderedSuspensions.map((event) => (
                    <WarnBlockCard key={event?.key} time={event?.time} type={event.suspensionType} />
                ))}
            </ScrollView>
        </View>
    );
}

const suspensionStatusLabels: Record<SuspensionStatusType, TLabel> = {
    [SuspensionStatusType.WARNED]: t('A moderator warned you'),
    [SuspensionStatusType.UNWARNED]: t('A moderator unwarned you'), // Not used
    [SuspensionStatusType.BLOCKED]: t('A moderator blocked you'),
    [SuspensionStatusType.UNBLOCKED]: t('A moderator unblocked you'),
};

const suspensionStatusColors: Record<SuspensionStatusType, string> = {
    [SuspensionStatusType.WARNED]: colorUtilitySaguaro2,
    [SuspensionStatusType.UNWARNED]: colorUtilityMum, // Not used
    [SuspensionStatusType.BLOCKED]: colorUtilityKilauea1,
    [SuspensionStatusType.UNBLOCKED]: colorUtilityMum,
};

export type WarnBlockCardProps = { type: SuspensionStatusType; time: number };

export function WarnBlockCard({ type, time = 0 }: WarnBlockCardProps) {
    const s = WarnAndBlockStyle;

    const cardLabel = suspensionStatusLabels[type];
    const cardColor = suspensionStatusColors[type];

    return (
        <View style={s.outerCard}>
            <View style={{ ...s.innerCard, backgroundColor: cardColor }}>
                <WarningAltFilled />
            </View>
            <View style={s.innerCardColumn}>
                <UtilityText label={cardLabel} type='small' weight='strong' />
                <UtilityText text={fromNow(time)} type='tiny' />
            </View>
        </View>
    );
}
